/* eslint-disable no-underscore-dangle */
import { isObjectLike, isString, isFinite, map, isNumber } from 'lodash';
import WindowProxy from 'commons-core-services/services/WindowProxy/WindowProxy.js';

/**
 * _getQueryStrings - Function that return the query string from the url.
 *
 * @return {String}   The query string.
 */
function _getQueryStrings() {
    const windowProxy = WindowProxy.get();
    return windowProxy && windowProxy.location.search.substring(1);
}

/**
 * _getQueryStringArray - function that return an array of string of ['name=value', ...] pair
 *                        from the queryString url
 *
 * @param  {String} queryStrings the query string url
 * @return {Array}               array of string ['name=value', ...] pair.
 */
function _getQueryStringArray(queryStrings) {
    const queryStringDecoded = decodeURI(queryStrings);
    return queryStringDecoded.split('&');
}

/**
 * _getParameterName - Function that return the parameter name of a string 'name=value' pair.
 *
 * @param  {String} queryString A string 'name=value' pair.
 * @return {String}             The name of the query string.
 */
function _getParameterName(queryString) {
    if (isString(queryString)) {
        const keyValue = queryString.split('=');
        return keyValue[0];
    }
    return '';
}

/**
 * _getParameterValue - Function that return the parameter value of a string 'name=value' pair.
 *
 * @param  {type} queryString description
 * @return {type}             description
 */
function _getParameterValue(queryString) {
    if (isString(queryString)) {
        const keyValue = queryString.split('=');
        // name=value1 -> value1
        // name= -> ''
        return keyValue.length > 1 ? keyValue.splice(1).join('=') : '';
    }
    return '';
}

class UrlService {
    /**
     * getUrlParameter - Function that returns parameter value from the url query string.
     *
     * @param  {String} paramName The name of the parameter name.
     * @return {String}           If the parameter is found, it return the value of the parameter name.
     *                            If the parameter is not found, it return empty string.
     */
    getUrlParameter(paramName) {
        const queryStrings = _getQueryStrings();

        if (!isString(paramName)) {
            return null;
        } else if (!queryStrings) {
            return '';
        }

        const queryStringArray = _getQueryStringArray(queryStrings);
        const queryString = queryStringArray.find((queryStr) => _getParameterName(queryStr) === paramName) || '';

        return _getParameterValue(queryString);
    }

    /**
     * getFirstUrlParameter - Function that returns the first existing parameter value for an array of parameters from
     *                        the url query string. The use case is to look for multiple parameters that represent the same value.
     *
     * @param  {String|Array} paramNames The names of the parameters.
     * @return {String} The string value of the first parameter that had a value.
     */
    getFirstUrlParameter(paramNames) {
        if (!Array.isArray(paramNames)) {
            return this.getUrlParameter(paramNames);
        }
        if (!paramNames) {
            return '';
        }
        if (paramNames.some((param) => !isString(param))) {
            return null;
        }
        const paramWithValue = paramNames.find((param) => this.getUrlParameter(param));
        return paramWithValue ? this.getUrlParameter(paramWithValue) : '';
    }

    replaceParamWithValue(url, param, value) {
        if (!isString(url)) {
            return '';
        } else if (!isString(param) || (!isString(value) && !isFinite(value))) {
            return url;
        }

        return url.replace(
            new RegExp(`(${param})`, 'g'),
            value || value === 0 ? encodeURIComponent(String(value).replace('/', '')) : value
        );
    }

    /**
     * getUrlParameters - Function that return an object that contain all {name: value} pair
     *                    of the url query string.
     *
     * @return {Object}   Object that contain all {name: value} pair.
     */
    getUrlParameters() {
        const queryStrings = _getQueryStrings();

        if (!queryStrings) {
            return {};
        }

        const queryStringArray = _getQueryStringArray(queryStrings);

        const params = queryStringArray
            .filter((queryString) => _getParameterName(queryString) && _getParameterValue(queryString))
            .map((queryString) => {
                const param = {};
                const name = _getParameterName(queryString);
                const value = _getParameterValue(queryString);
                param[name] = value;
                return param;
            });

        return Object.assign({}, ...params);
    }

    encodeUriParameter(name, value) {
        if (!name || !isString(name) || (!value && value !== 0 && value !== false)) {
            return '';
        }
        return `${name}=${encodeURIComponent(String(value))}`;
    }

    decodeUriParameter(name) {
        if (!name || !isString(name)) {
            return '';
        }
        const encodedValue = this.getUrlParameter(name);
        return encodedValue ? decodeURIComponent(encodedValue) : '';
    }

    buildUriParametersFromCollection(collection) {
        if (!isObjectLike(collection)) {
            return '';
        }

        return map(collection, (value, key) => {
            return this.encodeUriParameter(key, value);
        })
            .filter((value) => value)
            .join('&');
    }

    /**
     * Add a query parameter to the URL.
     * It does NOT verify if the parameter already exist
     * @param {String} name
     * @param {String} value
     */
    addParameter(name, value) {
        const windowProxy = WindowProxy.get();
        if (!windowProxy) {
            return;
        }
        const currentUrl = windowProxy.location.pathname;
        const params = windowProxy.location.search;

        const urlPath = `${currentUrl}${params ? `${params}&` : '?'}${name}=${value}`;
        windowProxy.history.replaceState({}, name, urlPath);
    }

    /**
     * Add a query paramter to the URL.
     * If there's a parameter with the same name, it'll replace the value
     * @param {String} name
     * @param {String} value
     */
    addOrReplaceParameter(name, value) {
        const windowProxy = WindowProxy.get();
        if (!windowProxy) {
            return;
        }

        if (this.getUrlParameter(name)) {
            const currentUrl = windowProxy.location.pathname;
            const params = windowProxy.location.search;

            const updatedParams = params.replace(new RegExp(`(${name}=).*?(&|$)`), `$1${value}$2`);

            const urlPath = `${currentUrl}${updatedParams}`;
            windowProxy.history.replaceState({}, name, urlPath);
        } else {
            this.addParameter(name, value);
        }
    }

    containsPath(path) {
        let searchPath = path;
        if (!searchPath || searchPath === '') return false;
        if (isNumber(searchPath)) searchPath = searchPath.toString();
        const windowProxy = WindowProxy.get();
        if (!windowProxy) {
            return false;
        }
        const currentUrl = windowProxy.location.pathname; // http://localhost:3000/fr/inventaire-occasion/mazda/cx-9/2018
        return currentUrl.includes(searchPath.toLowerCase()); // 'inventaire-occasion/mazda/cx-9/2018' contains 'path' ?
    }

    getUrlOriginAndPath() {
        const windowProxy = WindowProxy.get();
        if (!windowProxy) {
            return false;
        }
        return `${windowProxy.location.origin}${windowProxy.location.pathname}`;
    }

    isValidHttpUrl(string) {
        let url;

        try {
            url = new URL(string);
        } catch (_) {
            return false;
        }

        return url.protocol === 'http:' || url.protocol === 'https:';
    }
}

// export private function only to test.
export { _getQueryStrings, _getQueryStringArray, _getParameterName, _getParameterValue };

export default new UrlService();
